import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import styled from "styled-components";
import {
  UnorderedListOutlined,
  AppstoreOutlined,
  FilterOutlined,
} from "@ant-design/icons";
import { Drawer } from "antd";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ProductCard from "../components/ProductCard";
import ShopSkeleton from "../components/ShopSkeleton";

const Container = styled.div`
  background-color: #ffffff;
  min-height: 100vh;
`;

const ShopWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 40px 20px;

  @media (max-width: 768px) {
    padding: 20px 15px;
  }
`;

const ShopHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  flex-wrap: wrap;
  gap: 15px;

  .left-section {
    display: flex;
    align-items: center;
    gap: 15px;
  }

  .right-section {
    display: flex;
    align-items: center;
    gap: 15px;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;

    .left-section {
      width: 100%;
    }

    .right-section {
      width: 100%;
      justify-content: space-between;
    }
  }
`;

const ProductCount = styled.p`
  font-size: 14px;
  color: #666;
  margin: 0;
  font-weight: 500;
`;

const ViewToggle = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;

  .view-btn {
    width: 40px;
    height: 40px;
    border: 1px solid #e8e8e8;
    background: #fff;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    transition: all 0.3s ease;
    font-size: 18px;
    color: #666;

    &:hover,
    &.active {
      background: #000;
      color: #fff;
      border-color: #000;
    }
  }
`;

const SortWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  label {
    font-size: 14px;
    font-weight: 500;
    color: #333;
  }

  select {
    padding: 8px 12px;
    border: 1px solid #e8e8e8;
    border-radius: 4px;
    background: #fff;
    cursor: pointer;
    font-size: 14px;
    transition: all 0.3s ease;

    &:hover {
      border-color: #fd8e07;
    }

    &:focus {
      outline: none;
      border-color: #fd8e07;
      box-shadow: 0 0 0 2px rgba(253, 142, 7, 0.1);
    }
  }
`;

const ContentWrapper = styled.div`
  display: grid;
  grid-template-columns: 250px 1fr;
  gap: 30px;

  @media (max-width: 1024px) {
    grid-template-columns: 200px 1fr;
    gap: 20px;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;

  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileFilterButton = styled.button`
  display: none;
  background: #000;
  color: #fff;
  border: none;
  padding: 10px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;

  &:hover {
    background: #fd8e07;
  }

  @media (max-width: 768px) {
    display: flex;
  }
`;

const FilterDrawerWrapper = styled(Drawer)`
  .ant-drawer-content-wrapper {
    animation: slideInLeft 0.3s ease;
  }

  &.ant-drawer-open .ant-drawer-content-wrapper {
    animation: slideInLeft 0.3s ease;
  }

  @keyframes slideInLeft {
    from {
      transform: translateX(-100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }

  @keyframes slideOutLeft {
    from {
      transform: translateX(0);
      opacity: 1;
    }
    to {
      transform: translateX(-100%);
      opacity: 0;
    }
  }

  .ant-drawer-header {
    border-bottom: 1px solid #e8e8e8;
    padding: 16px 20px;
  }

  .ant-drawer-title {
    font-weight: 600;
    color: #333;
    font-size: 16px;
  }

  .ant-drawer-body {
    padding: 20px;
  }
`;

const FilterSection = styled.div`
  h3 {
    font-size: 16px;
    font-weight: 600;
    margin: 0 0 15px 0;
    color: #333;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
`;

const FilterOption = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
  cursor: pointer;
  font-size: 14px;
  color: #666;
  transition: color 0.3s ease;
  text-transform: capitalize;

  input {
    cursor: pointer;
    width: 18px;
    height: 18px;
    accent-color: #fd8e07;
  }

  &:hover {
    color: #333;
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const PriceRange = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  input[type="range"] {
    width: 100%;
    cursor: pointer;
    accent-color: #fd8e07;
  }

  .price-display {
    display: flex;
    justify-content: space-between;
    font-size: 13px;
    color: #666;
  }
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 25px;
  margin-bottom: 40px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 20px;
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 15px;
  }

  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }

  .ant-card-cover img {
    width: 100% !important;
    height: 250px !important;
    aspect-ratio: 1 / 1;
    object-fit: contain !important;
  }
`;

const ProductList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 40px;
`;

const ListProductCard = styled.div`
  display: grid;
  grid-template-columns: 200px 1fr auto;
  gap: 20px;
  padding: 20px;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    border-color: #fd8e07;
  }

  @media (max-width: 768px) {
    grid-template-columns: 150px 1fr;
    gap: 15px;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const ListImage = styled.div`
  width: 200px;
  height: 200px;
  /* background: #f5f5f5; */
  border-radius: 4px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  @media (max-width: 768px) {
    width: 150px;
    height: 150px;
  }

  @media (max-width: 480px) {
    width: 100%;
    height: 200px;
  }
`;

const ListDetails = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  h3 {
    font-size: 16px;
    font-weight: 600;
    margin: 0 0 10px 0;
    color: #333;
  }

  .category {
    font-size: 12px;
    color: #999;
    margin-bottom: 8px;
  }

  .description {
    font-size: 13px;
    color: #666;
    line-height: 1.5;
    margin-bottom: 15px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .rating {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 10px;
    font-size: 12px;
    color: #ffc107;
  }
`;

const ListPrice = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: space-between;

  .price {
    font-size: 24px;
    font-weight: 700;
    color: #000;
    margin-bottom: 10px;
  }

  .original-price {
    font-size: 14px;
    color: #999;
    text-decoration: line-through;
  }

  .add-to-cart {
    padding: 10px 20px;
    background: transparent;
    border: 1px solid #fd8e07;
    color: #fd8e07;
    border-radius: 4px;
    cursor: pointer;
    font-weight: 500;
    transition: all 0.3s ease;
    white-space: nowrap;

    &:hover {
      background: #fd8e07;
      color: #fff;
    }
  }

  @media (max-width: 768px) {
    align-items: flex-start;
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  margin-top: 40px;

  .page-info {
    font-size: 14px;
    color: #666;
    margin-right: 20px;
  }

  button,
  .page-btn {
    width: 40px;
    height: 40px;
    border: 1px solid #e8e8e8;
    background: #fff;
    cursor: pointer;
    border-radius: 4px;
    transition: all 0.3s ease;
    font-weight: 500;

    &:hover,
    &.active {
      background: #000;
      color: #fff;
      border-color: #000;
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }
  }
`;

const ITEMS_PER_PAGE = 12;

const Shop = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const category = searchParams.get("category");
  const categoryId = location.state?.categoryId;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [viewMode, setViewMode] = useState("grid");

  const handleProductClick = (product) => {
    navigate(`/product/${product.id}`, { state: { product } });
  };
  const [sortBy, setSortBy] = useState("relevance");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedGenders, setSelectedGenders] = useState([]);
  const [priceRange, setPriceRange] = useState([0, 15000]);
  const [products, setProducts] = useState([]);

  // Fetch products from API
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const baseUrl = process.env.REACT_APP_API_BASE_URL;

        let apiUrl;
        if (categoryId) {
          apiUrl = `${baseUrl}/products?category_id=${categoryId}`;
        } else {
          apiUrl = `${baseUrl}/products`;
        }

        const response = await fetch(apiUrl);
        if (!response.ok) {
          throw new Error("Failed to fetch products");
        }

        const data = await response.json();

        // Extract products from API response structure
        if (data.status && data.data && data.data.data) {
          const transformedProducts = data.data.data.map((product) => {
            // Calculate average rating from reviews
            const reviews = product.reviews || [];
            const averageRating =
              reviews.length > 0
                ? Math.round(
                    reviews.reduce((sum, review) => sum + review.rating, 0) /
                      reviews.length,
                  )
                : 0;

            return {
              ...product,
              image:
                product.images?.[0]?.url ||
                "https://via.placeholder.com/300x250?text=Product",
              price: parseFloat(product.base_price || 0),
              originalPrice: 0,
              rating: averageRating,
              reviews: reviews,
              badge: null,
              discount: 0,
            };
          });
          setProducts(transformedProducts);
        } else {
          setProducts([]);
        }
      } catch (error) {
        console.error("Error fetching products:", error);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [categoryId]);

  // Use only fetched products, don't fallback to mockProducts
  const productsToUse = products;

  // Extract unique categories and genders from products
  const uniqueCategories = [
    ...new Set(productsToUse.map((p) => p.category?.name || p.category)),
  ].sort();
  const uniqueGenders = [...new Set(productsToUse.map((p) => p.gender))].sort();

  // Auto-select gender if only one option is available
  useEffect(() => {
    if (uniqueGenders.length === 1 && selectedGenders.length === 0) {
      setSelectedGenders([uniqueGenders[0]]);
    }
  }, [uniqueGenders]);

  let filteredProducts = productsToUse.filter((product) => {
    // Category filter
    if (
      selectedCategories.length > 0 &&
      !selectedCategories.includes(product.category?.name || product.category)
    ) {
      return false;
    }
    // Gender filter
    if (
      selectedGenders.length > 0 &&
      !selectedGenders.includes(product.gender)
    ) {
      return false;
    }
    // Price filter
    const productPrice = parseFloat(product.price || product.base_price || 0);
    if (productPrice < priceRange[0] || productPrice > priceRange[1]) {
      return false;
    }
    return true;
  });

  if (sortBy === "price-low") {
    filteredProducts = [...filteredProducts].sort((a, b) => a.price - b.price);
  } else if (sortBy === "price-high") {
    filteredProducts = [...filteredProducts].sort((a, b) => b.price - a.price);
  } else if (sortBy === "rating") {
    filteredProducts = [...filteredProducts].sort(
      (a, b) => b.rating - a.rating,
    );
  } else if (sortBy === "newest") {
    filteredProducts = [...filteredProducts].reverse();
  }

  const totalPages = Math.ceil(filteredProducts.length / ITEMS_PER_PAGE);
  const startIdx = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIdx = startIdx + ITEMS_PER_PAGE;
  const paginatedProducts = filteredProducts.slice(startIdx, endIdx);

  if (loading) {
    return (
      <Container>
        <ShopSkeleton viewMode={viewMode} />
        <Footer />
      </Container>
    );
  }

  return (
    <Container>
      <ShopWrapper>
        <ShopHeader>
          <div className="left-section">
            <ViewToggle>
              <button
                className={`view-btn ${viewMode === "grid" ? "active" : ""}`}
                onClick={() => setViewMode("grid")}
                title="Grid View"
              >
                <AppstoreOutlined />
              </button>
              <button
                className={`view-btn ${viewMode === "list" ? "active" : ""}`}
                onClick={() => setViewMode("list")}
                title="List View"
              >
                <UnorderedListOutlined />
              </button>{" "}
              <ProductCount>
                There are {filteredProducts.length} products.
              </ProductCount>
            </ViewToggle>
          </div>
          <div className="right-section">
            <MobileFilterButton onClick={() => setMobileFilterOpen(true)}>
              <FilterOutlined />
              Filter
            </MobileFilterButton>
            <SortWrapper>
              <label htmlFor="sort">Sort by:</label>
              <select
                id="sort"
                value={sortBy}
                onChange={(e) => {
                  setSortBy(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="relevance">Relevance</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="rating">Rating: High to Low</option>
                <option value="newest">Newest</option>
              </select>
            </SortWrapper>
          </div>
        </ShopHeader>

        <ContentWrapper>
          <Sidebar>
            {!categoryId && (
              <FilterSection>
                <h3>Category</h3>
                <FilterOption>
                  <input
                    type="checkbox"
                    checked={selectedCategories.length === 0}
                    onChange={() => setSelectedCategories([])}
                  />
                  All Categories
                </FilterOption>
                {uniqueCategories.map((category) => (
                  <FilterOption key={category}>
                    <input
                      type="checkbox"
                      checked={selectedCategories.includes(category)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedCategories([
                            ...selectedCategories,
                            category,
                          ]);
                        } else {
                          setSelectedCategories(
                            selectedCategories.filter((c) => c !== category),
                          );
                        }
                      }}
                    />
                    {category}
                  </FilterOption>
                ))}
              </FilterSection>
            )}

            <FilterSection>
              <h3>Price</h3>
              <PriceRange>
                <input
                  type="range"
                  min="0"
                  max="15000"
                  defaultValue="15000"
                  onChange={(e) => setPriceRange([0, parseInt(e.target.value)])}
                />
                <div className="price-display">
                  <span>₹{priceRange[0]}</span>
                  <span>₹{priceRange[1]}</span>
                </div>
              </PriceRange>
            </FilterSection>

            <FilterSection>
              <h3>Gender</h3>
              {uniqueGenders.map((gender) => (
                <FilterOption key={gender}>
                  <input
                    type="checkbox"
                    checked={selectedGenders.includes(gender)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedGenders([...selectedGenders, gender]);
                      } else {
                        setSelectedGenders(
                          selectedGenders.filter((g) => g !== gender),
                        );
                      }
                    }}
                  />
                  {gender}
                </FilterOption>
              ))}
            </FilterSection>
          </Sidebar>

          <MainContent>
            {loading ? (
              <div style={{ textAlign: "center", padding: "40px" }}>
                <p>Loading products...</p>
              </div>
            ) : paginatedProducts.length === 0 ? (
              <div style={{ textAlign: "center", padding: "40px" }}>
                <p style={{ fontSize: "18px", color: "#666" }}>
                  No products available
                </p>
              </div>
            ) : viewMode === "grid" ? (
              <ProductGrid>
                {paginatedProducts.map((product) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    onClick={() => handleProductClick(product)}
                  />
                ))}
              </ProductGrid>
            ) : (
              <ProductList>
                {paginatedProducts.map((product) => (
                  <ListProductCard
                    key={product.id}
                    onClick={() => handleProductClick(product)}
                    style={{ cursor: "pointer" }}
                  >
                    <ListImage>
                      <img src={product.image} alt={product.name} />
                      {product.badge && (
                        <div
                          style={{
                            position: "absolute",
                            top: 10,
                            left: 10,
                            background: "#000",
                            color: "#fff",
                            padding: "5px 12px",
                            borderRadius: "4px",
                            fontSize: "12px",
                            fontWeight: "600",
                          }}
                        >
                          {product.badge}
                        </div>
                      )}
                    </ListImage>
                    <ListDetails>
                      <div>
                        <div className="category">
                          {typeof product.category === "object"
                            ? product.category?.name
                            : product.category}
                        </div>
                        <h3>{product.name}</h3>
                        <div className="rating">
                          {"★".repeat(product.rating)}
                          {"☆".repeat(5 - product.rating)}
                          <span
                            style={{
                              marginLeft: "8px",
                              color: "#666",
                              fontSize: "13px",
                            }}
                          >
                            {product.reviews && product.reviews.length > 0
                              ? `${(
                                  product.reviews.reduce(
                                    (sum, r) => sum + r.rating,
                                    0,
                                  ) / product.reviews.length
                                ).toFixed(1)} (${product.reviews.length})`
                              : "No reviews"}
                          </span>
                        </div>
                        <p className="description">
                          {product.short_description}
                        </p>
                      </div>
                    </ListDetails>
                    <ListPrice>
                      <div className="price">
                        ₹
                        {(typeof product.price === "number"
                          ? product.price
                          : parseFloat(product.price || 0)
                        ).toLocaleString("en-IN")}
                      </div>
                      {product.originalPrice > 0 && (
                        <div className="original-price">
                          ₹
                          {(typeof product.originalPrice === "number"
                            ? product.originalPrice
                            : parseFloat(product.originalPrice || 0)
                          ).toLocaleString("en-IN")}
                        </div>
                      )}
                    </ListPrice>
                  </ListProductCard>
                ))}
              </ProductList>
            )}

            <Pagination>
              <span className="page-info">
                Showing {startIdx + 1}-
                {Math.min(endIdx, filteredProducts.length)} of{" "}
                {filteredProducts.length} item(s)
              </span>
              <button
                onClick={() => {
                  setCurrentPage(Math.max(1, currentPage - 1));
                  setTimeout(() => {
                    document.querySelector(".right-section")?.scrollIntoView({
                      behavior: "smooth",
                      block: "start",
                    });
                  }, 0);
                }}
                disabled={currentPage === 1}
              >
                ←
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                (page) => (
                  <button
                    key={page}
                    className={`page-btn ${currentPage === page ? "active" : ""}`}
                    onClick={() => {
                      setCurrentPage(page);
                      setTimeout(() => {
                        document
                          .querySelector(".right-section")
                          ?.scrollIntoView({
                            behavior: "smooth",
                            block: "start",
                          });
                      }, 0);
                    }}
                  >
                    {page}
                  </button>
                ),
              )}
              <button
                onClick={() => {
                  setCurrentPage(Math.min(totalPages, currentPage + 1));
                  setTimeout(() => {
                    document.querySelector(".ant-card")?.scrollIntoView({
                      behavior: "smooth",
                      block: "start",
                    });
                  }, 0);
                }}
                disabled={currentPage === totalPages}
              >
                →
              </button>
            </Pagination>
          </MainContent>
        </ContentWrapper>
      </ShopWrapper>

      {/* Mobile Filter Drawer */}
      <FilterDrawerWrapper
        title="Filters"
        placement="left"
        onClose={() => setMobileFilterOpen(false)}
        open={mobileFilterOpen}
        width={280}
      >
        <FilterSection>
          <h3>Category</h3>
          <FilterOption>
            <input
              type="checkbox"
              checked={selectedCategories.length === 0}
              onChange={() => setSelectedCategories([])}
            />
            All Categories
          </FilterOption>
          {uniqueCategories.map((category) => (
            <FilterOption key={category}>
              <input
                type="checkbox"
                checked={selectedCategories.includes(category)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedCategories([...selectedCategories, category]);
                  } else {
                    setSelectedCategories(
                      selectedCategories.filter((c) => c !== category),
                    );
                  }
                }}
              />
              {category}
            </FilterOption>
          ))}
        </FilterSection>

        <FilterSection style={{ marginTop: "20px" }}>
          <h3>Price</h3>
          <PriceRange>
            <input
              type="range"
              min="0"
              max="15000"
              defaultValue="15000"
              onChange={(e) => setPriceRange([0, parseInt(e.target.value)])}
            />
            <div className="price-display">
              <span>₹{priceRange[0]}</span>
              <span>₹{priceRange[1]}</span>
            </div>
          </PriceRange>
        </FilterSection>

        <FilterSection style={{ marginTop: "20px" }}>
          <h3>Gender</h3>
          {uniqueGenders.map((gender) => (
            <FilterOption key={gender}>
              <input
                type="checkbox"
                checked={selectedGenders.includes(gender)}
                onChange={(e) => {
                  if (e.target.checked) {
                    setSelectedGenders([...selectedGenders, gender]);
                  } else {
                    setSelectedGenders(
                      selectedGenders.filter((g) => g !== gender),
                    );
                  }
                }}
              />
              {gender}
            </FilterOption>
          ))}
        </FilterSection>
      </FilterDrawerWrapper>

      <Footer />
    </Container>
  );
};

export default Shop;
